/* eslint-disable no-bitwise */
import { ensureExhaustive } from "@dhau/lang-extras";
import { uint8ArrayAlloc } from "../utils/uint8-array.ts";
import type { Bitmap } from "./types.ts";
import { bitmapChannels } from "./types.ts";

function rotateBitmapAtRightAnglesClockwise(
	bitmap: Bitmap,
	deg: 0 | 90 | 180 | 270,
) {
	return rotateBitmapAtRightAnglesCounterClockwise(
		bitmap,
		((360 - deg) % 360) as 0 | 90 | 180 | 270,
	);
}

function rotateBitmapAtRightAnglesCounterClockwise(
	bitmap: Bitmap,
	deg: 0 | 90 | 180 | 270,
) {
	if (deg === 0) {
		return bitmap;
	}

	const { width, height } = bitmap;

	const newWidth = deg === 180 ? width : height;
	const newHeight = deg === 180 ? height : width;

	// Allocate destination byte buffer and prepare 32-bit views for fast pixel copies
	const dstBytes = uint8ArrayAlloc(newWidth * newHeight * bitmapChannels);

	// Create typed 32-bit views over source and destination.
	// A Uint32Array view requires a 4-byte-aligned byteOffset, but the Bitmap
	// type permits `data` to be a subarray with an arbitrary offset. Copy to a
	// fresh (offset 0) buffer when the source isn't aligned.
	const rawSrcBytes = bitmap.data as Uint8Array;
	const srcBytes =
		rawSrcBytes.byteOffset % Uint32Array.BYTES_PER_ELEMENT === 0
			? rawSrcBytes
			: new Uint8Array(rawSrcBytes);
	const src32 = new Uint32Array(
		srcBytes.buffer,
		srcBytes.byteOffset,
		width * height,
	);
	const dst32 = new Uint32Array(dstBytes.buffer, 0, newWidth * newHeight);

	switch (deg) {
		case 90: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = (width - 1 - x) * newWidth + y;
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		case 270: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = x * newWidth + (height - 1 - y);
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		case 180: {
			for (let y = 0; y < height; y++) {
				const yTimesW = y * width;
				const dstRowBase = (height - 1 - y) * newWidth;
				for (let x = 0; x < width; x++) {
					const srcIndex = yTimesW + x;
					const dstIndex = dstRowBase + (width - 1 - x);
					dst32[dstIndex] = src32[srcIndex];
				}
			}
			break;
		}
		default:
			ensureExhaustive(deg);
	}

	return {
		data: dstBytes,
		width: newWidth,
		height: newHeight,
	};
}

export {
	rotateBitmapAtRightAnglesClockwise,
	rotateBitmapAtRightAnglesCounterClockwise,
};
